import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, Award, Scissors, User, Clock, DollarSign, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_BASE_URL } from '../../../config';
import ModuleSelection from '../ModuleSelection/ModuleSelection';
import ModuleServices from '../ModuleServices/ModuleServices';
import ReservarCitaModal from '../ReservarCitaModal/ReservarCitaModal';
import './AgendarCitaModal.css';

const mapBarberFromApi = (b) => ({
  id: b.id,
  nombre: b.name || b.nombre || '',
  especialidad: b.specialty || b.especialidad || 'Barbero Profesional',
  descripcion: b.bio || b.descripcion || '',
  experiencia: b.experience_years ? `${b.experience_years} años` : b.experiencia || 'Experiencia profesional',
  rating: b.rating || 4.5,
  totalReseñas: b.total_reviews || b.totalReseñas || 0,
  disponible: b.available !== false,
  image_url: b.image_url || null,
});

const formatPrice = (price) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

const AgendarCitaModal = ({ isOpen, onClose, clientData, onSuccess }) => {
  const [step, setStep] = useState('barber-list');
  const [bookingMode, setBookingMode] = useState('barber');
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [barberos, setBarberos] = useState([]);
  const [selectedBarbero, setSelectedBarbero] = useState(null);
  const [barberServices, setBarberServices] = useState([]);
  const [loadingBarberos, setLoadingBarberos] = useState(false);
  const [loadingBarberServices, setLoadingBarberServices] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('barber-list');
      setBookingMode('barber');
      setSelectedModule(null);
      setSelectedService(null);
      setSelectedBarbero(null);
      setBarberos([]);
      setBarberServices([]);
      setError(null);
    }
  }, [isOpen]);

  // Barberos filtrados por módulo (flujo por servicio)
  useEffect(() => {
    if (step === 'barber' && bookingMode === 'service' && selectedModule?.id) {
      const fetchBarberos = async () => {
        try {
          setLoadingBarberos(true);
          setError(null);
          const res = await fetch(`${API_BASE_URL}/barber/barbers/by-module/${selectedModule.id}`);
          if (!res.ok) throw new Error('Error al cargar barberos');
          const data = await res.json();
          setBarberos(data.map(mapBarberFromApi));
        } catch (err) {
          setError(err.message);
          setBarberos([]);
        } finally {
          setLoadingBarberos(false);
        }
      };
      fetchBarberos();
    }
  }, [step, bookingMode, selectedModule]);

  // Todos los barberos (flujo por barbero)
  useEffect(() => {
    if (isOpen && step === 'barber-list' && bookingMode === 'barber') {
      const fetchAllBarberos = async () => {
        try {
          setLoadingBarberos(true);
          setError(null);
          const res = await fetch(`${API_BASE_URL}/barber/barbers`);
          if (!res.ok) throw new Error('Error al cargar barberos');
          const data = await res.json();
          setBarberos(data.map(mapBarberFromApi).filter((b) => b.disponible));
        } catch (err) {
          setError(err.message);
          setBarberos([]);
        } finally {
          setLoadingBarberos(false);
        }
      };
      fetchAllBarberos();
    }
  }, [isOpen, step, bookingMode]);

  // Servicios del barbero seleccionado (flujo por barbero)
  useEffect(() => {
    if (step === 'barber-service' && bookingMode === 'barber' && selectedBarbero?.id) {
      const fetchBarberServices = async () => {
        try {
          setLoadingBarberServices(true);
          setError(null);
          const res = await fetch(`${API_BASE_URL}/barber/barbers/${selectedBarbero.id}/services`);
          if (!res.ok) throw new Error('Error al cargar servicios');
          const data = await res.json();
          setBarberServices(data);
        } catch (err) {
          setError(err.message);
          setBarberServices([]);
        } finally {
          setLoadingBarberServices(false);
        }
      };
      fetchBarberServices();
    }
  }, [step, bookingMode, selectedBarbero]);

  const handleTabChange = (mode) => {
    if (mode === bookingMode) return;
    setBookingMode(mode);
    setError(null);
    setSelectedModule(null);
    setSelectedService(null);
    setSelectedBarbero(null);
    setBarberos([]);
    setBarberServices([]);
    setStep(mode === 'barber' ? 'barber-list' : 'module');
  };

  const handleSelectModule = (module) => {
    setSelectedModule(module);
    setStep('service');
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    setStep('barber');
  };

  const handleSelectBarberFromService = (barbero) => {
    setSelectedBarbero(barbero);
    setStep('form');
  };

  const handleSelectBarberDirect = (barbero) => {
    setSelectedBarbero(barbero);
    setSelectedService(null);
    setStep('barber-service');
  };

  const handleSelectServiceFromBarber = (service) => {
    setSelectedService(service);
    setStep('form');
  };

  const handleBackToModules = () => {
    setStep('module');
    setSelectedModule(null);
    setSelectedService(null);
    setBarberos([]);
  };

  const handleBackToServices = () => {
    setStep('service');
    setSelectedService(null);
    setSelectedBarbero(null);
    setBarberos([]);
  };

  const handleBackToBarbersFromService = () => {
    setStep('barber');
    setSelectedBarbero(null);
  };

  const handleBackToBarberList = () => {
    setStep('barber-list');
    setSelectedBarbero(null);
    setSelectedService(null);
    setBarberServices([]);
  };

  const handleBackFromForm = () => {
    if (bookingMode === 'barber') {
      setSelectedService(null);
      setStep('barber-service');
    } else {
      setSelectedBarbero(null);
      setStep('barber');
    }
  };

  const handleReservationSuccess = () => {
    if (typeof onSuccess === 'function') onSuccess();
    onClose();
  };

  const handleCloseForm = () => {
    handleBackFromForm();
  };

  const renderBarberGrid = (onSelect) => {
    if (loadingBarberos) {
      return (
        <div className="agendar-modal-loading">
          <div className="agendar-modal-spinner" />
          <p>Cargando barberos...</p>
        </div>
      );
    }
    if (error) {
      return <p className="agendar-modal-error">Error: {error}</p>;
    }
    if (barberos.length === 0) {
      return <p className="agendar-modal-empty">No hay barberos disponibles</p>;
    }
    return (
      <div className="agendar-modal-barbers-grid">
        {barberos.map((b) => (
          <motion.div
            key={b.id}
            className="agendar-modal-barber-card"
            onClick={() => onSelect(b)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="agendar-modal-barber-avatar">
              {b.image_url ? (
                <img
                  src={b.image_url.startsWith('http') ? b.image_url : `${API_BASE_URL}${b.image_url}`}
                  alt={b.nombre}
                  className="agendar-modal-barber-avatar-img"
                />
              ) : (
                b.nombre.charAt(0).toUpperCase()
              )}
            </div>
            <h3>{b.nombre}</h3>
            <p className="agendar-modal-barber-spec">{b.especialidad}</p>
            <div className="agendar-modal-barber-stats">
              <span><Award size={14} /> {b.experiencia}</span>
            </div>
            <span className="agendar-modal-barber-select">Seleccionar</span>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderBarberServicesGrid = () => {
    if (loadingBarberServices) {
      return (
        <div className="agendar-modal-loading">
          <div className="agendar-modal-spinner" />
          <p>Cargando servicios...</p>
        </div>
      );
    }
    if (error) {
      return <p className="agendar-modal-error">Error: {error}</p>;
    }
    if (barberServices.length === 0) {
      return <p className="agendar-modal-empty">Este barbero no tiene servicios asignados</p>;
    }
    return (
      <div className="agendar-modal-services-grid">
        {barberServices.map((service, index) => (
          <motion.div
            key={service.id}
            className="agendar-modal-service-card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: index * 0.04 }}
            onClick={() => handleSelectServiceFromBarber(service)}
          >
            <h3 className="agendar-modal-service-name">{service.name}</h3>
            <div className="agendar-modal-service-meta">
              <span><DollarSign size={16} /> {formatPrice(service.price)}</span>
              <span><Clock size={16} /> {service.duration_minutes} min</span>
            </div>
            <div className="agendar-modal-service-footer">
              <span>Seleccionar servicio</span>
              <Check size={16} />
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  const showModeTabs = step === 'barber-list' || step === 'module';

  return (
    <div className="agendar-cita-modal-overlay" onClick={onClose}>
      <div
        className="agendar-cita-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="agendar-cita-modal-header">
          <h2>Agendar Nueva Cita</h2>
          <button type="button" className="agendar-cita-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {showModeTabs && (
          <div className="agendar-mode-tabs">
            <button
              type="button"
              className={`agendar-mode-tab ${bookingMode === 'barber' ? 'active' : ''}`}
              onClick={() => handleTabChange('barber')}
            >
              <User size={18} />
              Por barbero
            </button>
            <button
              type="button"
              className={`agendar-mode-tab ${bookingMode === 'service' ? 'active' : ''}`}
              onClick={() => handleTabChange('service')}
            >
              <Scissors size={18} />
              Por servicio
            </button>
          </div>
        )}

        <div className="agendar-cita-modal-body">
          {/* Flujo por servicio: módulos */}
          {step === 'module' && bookingMode === 'service' && (
            <div className="agendar-modal-step">
              <p className="agendar-modal-subtitle">Selecciona el módulo de servicio</p>
              <ModuleSelection onSelectModule={handleSelectModule} />
            </div>
          )}

          {/* Flujo por servicio: servicios del módulo */}
          {step === 'service' && bookingMode === 'service' && selectedModule && (
            <div className="agendar-modal-step">
              <button type="button" className="agendar-modal-back" onClick={handleBackToModules}>
                <ArrowLeft size={20} />
                Volver a módulos
              </button>
              <p className="agendar-modal-subtitle">
                Servicios de: <strong>{selectedModule.display_name}</strong>
              </p>
              <ModuleServices
                module={selectedModule}
                onSelectService={handleSelectService}
                onBack={handleBackToModules}
              />
            </div>
          )}

          {/* Flujo por servicio: barberos del módulo */}
          {step === 'barber' && bookingMode === 'service' && selectedService && (
            <div className="agendar-modal-step">
              <button type="button" className="agendar-modal-back" onClick={handleBackToServices}>
                <ArrowLeft size={20} />
                Volver a servicios
              </button>
              <p className="agendar-modal-subtitle">Selecciona el barbero para tu cita</p>
              {renderBarberGrid(handleSelectBarberFromService)}
            </div>
          )}

          {/* Flujo por barbero: lista de barberos */}
          {step === 'barber-list' && bookingMode === 'barber' && (
            <div className="agendar-modal-step">
              <p className="agendar-modal-subtitle">Selecciona tu barbero</p>
              {renderBarberGrid(handleSelectBarberDirect)}
            </div>
          )}

          {/* Flujo por barbero: servicios del barbero */}
          {step === 'barber-service' && bookingMode === 'barber' && selectedBarbero && (
            <div className="agendar-modal-step">
              <button type="button" className="agendar-modal-back" onClick={handleBackToBarberList}>
                <ArrowLeft size={20} />
                Volver a barberos
              </button>
              <p className="agendar-modal-subtitle">
                Servicios de: <strong>{selectedBarbero.nombre}</strong>
              </p>
              {renderBarberServicesGrid()}
            </div>
          )}

          {/* Formulario de reserva */}
          {step === 'form' && selectedBarbero && selectedService && (
            <div className="agendar-modal-step agendar-modal-form-step">
              <button type="button" className="agendar-modal-back" onClick={handleBackFromForm}>
                <ArrowLeft size={20} />
                {bookingMode === 'barber' ? 'Cambiar servicio' : 'Cambiar barbero'}
              </button>
              <ReservarCitaModal
                barbero={selectedBarbero}
                service={selectedService}
                isOpen={true}
                onClose={handleCloseForm}
                clientData={clientData}
                onSuccess={handleReservationSuccess}
                embedded={true}
              />
            </div>
          )}
        </div>

        {(step === 'barber' || step === 'barber-list') && (
          <div className="agendar-cita-modal-actions">
            <button type="button" className="agendar-modal-btn-cancel" onClick={onClose}>
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendarCitaModal;
